import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle, Upload, Image as ImageIcon, Sparkles,
  ArrowRight, ArrowLeft, Loader2, Building2, AlertCircle
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { venueApi } from '../../lib/api';
import { resolveAssetUrl } from '../../config';

const VenueApplication = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, openAuthModal } = useAuth();

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [coasterDesigns, setCoasterDesigns] = useState([]);

  const [formData, setFormData] = useState({
    venue_name: '',
    venue_type: 'Bar',
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
    owner_manager_name: '',
    phone: '',
    email: user?.email || '',
    average_customers_per_day: '',
    coaster_delivery_frequency: 'monthly',
    requested_coaster_quantity: 5000,
  });

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [selectedDesignId, setSelectedDesignId] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      openAuthModal('signup');
    }
  }, [isAuthenticated, openAuthModal]);

  // Sync email field when user authenticates (e.g., after auth modal login)
  useEffect(() => {
    if (user?.email && !formData.email) {
      setFormData(prev => ({ ...prev, email: user.email }));
    }
  }, [user]);

  useEffect(() => {
    fetchCoasterDesigns();
  }, []);

  const fetchCoasterDesigns = async () => {
    try {
      const response = await venueApi.get(`/venue/designs`);
      setCoasterDesigns(response.data);
      if (response.data.length > 0) {
        setSelectedDesignId(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch coaster designs:', error);
      // No placeholder designs: submitting a fake id would match no row.
      setCoasterDesigns([]);
      setSelectedDesignId(null);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Logo file size must be less than 5MB');
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData.venue_name || !formData.street_address || !formData.city ||
            !formData.state || !formData.zip_code || !formData.owner_manager_name ||
            !formData.phone || !formData.email || !formData.average_customers_per_day) {
          setError('Please fill in all required fields');
          return false;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          setError('Please enter a valid email address');
          return false;
        }
        if (!/\d{10,}/.test(formData.phone.replace(/\D/g, ''))) {
          setError('Please enter a valid phone number (at least 10 digits)');
          return false;
        }
        if (!/^\d{5}(-\d{4})?$/.test(formData.zip_code.trim())) {
          setError('Please enter a valid zip code (e.g., 08055 or 08055-1234)');
          return false;
        }
        break;
      case 2:
        if (!logoFile) {
          setError('Please upload your venue logo');
          return false;
        }
        break;
      case 3:
        if (!selectedDesignId) {
          setError('Please select a coaster design');
          return false;
        }
        break;
    }
    setError('');
    return true;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setError('');
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setLoading(true);
    setError('');

    let uploadedLogoUrl = null;

    try {

      const logoFormData = new FormData();
      logoFormData.append('logo', logoFile);

      const logoResponse = await venueApi.post(
        `/venue/upload-logo`,
        logoFormData
      );

      uploadedLogoUrl = logoResponse.data.logo_url;

      if (!isAuthenticated || !user?.id) {
        openAuthModal('signup');
        setError('Please sign in to submit your application.');
        setLoading(false);
        return;
      }
      const applicationData = {
        ...formData,
        user_id: user.id,
        logo_url: uploadedLogoUrl,
        selected_design_id: selectedDesignId,
        average_customers_per_day: parseInt(formData.average_customers_per_day),
      };

      await venueApi.post(
        `/venue/apply`,
        applicationData
      );

      setCurrentStep(4);
    } catch (error) {
      console.error('Application submission failed:', error);
      setError(error.response?.data?.detail || 'Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const venueTypes = ['Bar', 'Restaurant', 'Cafe', 'Pub', 'Brewery', 'Lounge', 'Other'];

  const TOTAL_STEPS = 3;

  // Step definitions matching OnboardingNew pattern
  const stepTitles = {
    1: { icon: Building2, title: 'Tell us about your venue' },
    2: { icon: Upload, title: 'Upload your venue logo' },
    3: { icon: Sparkles, title: 'Choose your coaster design' }
  };

  // V5 Input Style
  const inputStyle = {
    width: '100%',
    height: '48px',
    padding: '0 16px',
    backgroundColor: '#FFFFFF',
    border: '2px solid rgba(30, 27, 75, 0.1)',
    borderRadius: '12px',
    fontSize: '16px',
    color: '#1e1b4b',
    outline: 'none',
    transition: 'all 0.2s ease'
  };

  const handleInputFocus = (e) => {
    e.target.style.borderColor = '#14b8a6';
    e.target.style.boxShadow = '0 0 0 4px rgba(20, 184, 166, 0.1)';
  };

  const handleInputBlur = (e) => {
    e.target.style.borderColor = 'rgba(30, 27, 75, 0.1)';
    e.target.style.boxShadow = 'none';
  };

  // Confirmation screen (step 4)
  if (currentStep === 4) {
    return (
      <div className="min-h-screen py-8 px-4" style={{ backgroundColor: '#FEF7ED' }}>
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col items-center mb-8">
            <img src="/tappd-logo.png" alt="Tappd" className="h-14 md:h-16 mb-2" />
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-2xl mx-auto bg-white p-8 text-center"
            style={{
              borderRadius: '12px',
              border: '1px solid rgba(30, 27, 75, 0.05)',
              boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
            }}
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="w-24 h-24 mx-auto mb-6 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)' }}
            >
              <CheckCircle size={48} className="text-white" />
            </motion.div>

            <h1
              className="text-[32px] mb-4"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500, color: '#1e1b4b' }}
            >
              Application Submitted!
            </h1>

            <p className="text-[16px] mb-6" style={{ color: 'rgba(30, 27, 75, 0.7)', fontFamily: "'DM Sans', sans-serif" }}>
              Thank you for applying to become a Tappd Hub Location.
            </p>

            <div
              className="p-4 mb-6 text-left"
              style={{
                backgroundColor: 'rgba(20, 184, 166, 0.05)',
                border: '1px solid rgba(20, 184, 166, 0.1)',
                borderRadius: '12px'
              }}
            >
              <div className="flex items-start gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 mt-1"
                  style={{ backgroundColor: 'rgba(20, 184, 166, 0.1)' }}
                >
                  <svg className="w-5 h-5" style={{ color: '#14b8a6' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-[14px]" style={{ color: '#1e1b4b' }}>What happens next?</p>
                  <p className="text-[13px] mt-1" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                    We&apos;ll review your application and get back to you within 48 hours. You&apos;ll receive an email at <strong>{formData.email}</strong> once your application is reviewed.
                  </p>
                </div>
              </div>
            </div>

            <p className="text-[13px] mb-6" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
              Questions? Reach out to <a href="mailto:hello@tappd.app" className="hover:underline" style={{ color: '#14b8a6' }}>hello@tappd.app</a>
            </p>

            <button
              onClick={() => navigate('/')}
              className="w-full py-3 px-6 text-white font-semibold transition-all hover:-translate-y-0.5"
              style={{
                background: 'linear-gradient(135deg, #14b8a6, #0d9488)',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(20, 184, 166, 0.4)'
              }}
            >
              Return to Home
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  const CurrentIcon = stepTitles[currentStep].icon;

  return (
    <div className="min-h-screen py-8 px-4" style={{ backgroundColor: '#FEF7ED' }}>
      <div className="max-w-4xl mx-auto">
        {/* Progress Header */}
        <div className="mb-8">
          <div className="flex flex-col items-center mb-4">
            <img
              src="/tappd-logo.png"
              alt="Tappd"
              className="h-14 md:h-16 mb-2"
            />
          </div>

          {/* V5 Code Accent */}
          <p className="text-center font-mono text-xs mb-2" style={{ color: '#14b8a6' }}>
            // venue_application
          </p>

          <p
            className="text-center mb-6 text-[14px]"
            style={{ color: 'rgba(30, 27, 75, 0.7)', fontFamily: "'DM Sans', sans-serif" }}
          >
            Apply to become a Tappd Hub Location and get 5,000 free coasters
          </p>

          {/* V5 Progress Stepper */}
          <ol className="flex items-center justify-center gap-2 mb-2" aria-label="Application progress">
            {[1, 2, 3].map((step) => (
              <React.Fragment key={step}>
                <li
                  aria-current={step === currentStep ? 'step' : undefined}
                  className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all"
                  style={
                    step < currentStep
                      ? { backgroundColor: '#22c55e', color: 'white' }
                      : step === currentStep
                      ? { background: 'linear-gradient(135deg, #14b8a6, #0d9488)', color: 'white', boxShadow: '0 4px 15px rgba(20, 184, 166, 0.4)' }
                      : { backgroundColor: 'rgba(30, 27, 75, 0.1)', color: 'rgba(30, 27, 75, 0.5)' }
                  }
                >
                  <span className="sr-only">
                    {`Step ${step} of ${TOTAL_STEPS}${step < currentStep ? ' (complete)' : step === currentStep ? ' (current)' : ''}`}
                  </span>
                  <span aria-hidden="true">
                    {step < currentStep ? <CheckCircle size={20} /> : step}
                  </span>
                </li>
                {step < TOTAL_STEPS && (
                  <li
                    aria-hidden="true"
                    className="w-12 h-1 transition-all"
                    style={{ backgroundColor: step < currentStep ? '#22c55e' : 'rgba(30, 27, 75, 0.1)' }}
                  />
                )}
              </React.Fragment>
            ))}
          </ol>
          <p className="text-center text-[13px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
            Step {currentStep} of {TOTAL_STEPS}
          </p>
        </div>

        {/* V5 Error Message */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mb-4 p-4 flex items-center gap-2 max-w-3xl mx-auto"
              style={{
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.2)',
                borderRadius: '12px'
              }}
            >
              <AlertCircle size={20} style={{ color: '#ef4444', flexShrink: 0 }} />
              <p style={{ color: '#ef4444' }}>{error}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* V5 Step Content Card */}
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className={`bg-white p-5 sm:p-8 mx-auto ${currentStep === 3 ? 'max-w-3xl' : 'max-w-2xl'}`}
          style={{
            borderRadius: '12px',
            border: '1px solid rgba(30, 27, 75, 0.05)',
            borderLeft: '4px solid #14b8a6',
            boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
          }}
        >
          {/* Step Header */}
          <div className="flex items-center gap-3 mb-6">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: 'rgba(20, 184, 166, 0.1)' }}
            >
              <CurrentIcon size={24} style={{ color: '#14b8a6' }} />
            </div>
            <h2
              className="text-[24px]"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500, color: '#1e1b4b' }}
            >
              {stepTitles[currentStep].title}
            </h2>
          </div>

          {/* Step 1: Venue Information */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[14px] font-medium mb-2" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                    Venue Name <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    name="venue_name"
                    value={formData.venue_name}
                    onChange={handleInputChange}
                    placeholder="e.g., The Local Tap"
                    style={inputStyle}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                  />
                </div>
                <div>
                  <label className="block text-[14px] font-medium mb-2" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                    Venue Type <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <select
                    name="venue_type"
                    value={formData.venue_type}
                    onChange={handleInputChange}
                    style={inputStyle}
                  >
                    {venueTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-[14px] font-medium mb-2" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                  Street Address <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  name="street_address"
                  value={formData.street_address}
                  onChange={handleInputChange}
                  placeholder="123 Main Street"
                  style={inputStyle}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                />
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-[14px] font-medium mb-2" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                    City <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="Medford"
                    style={inputStyle}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                  />
                </div>
                <div>
                  <label className="block text-[14px] font-medium mb-2" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                    State <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    placeholder="NJ"
                    style={inputStyle}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                  />
                </div>
                <div>
                  <label className="block text-[14px] font-medium mb-2" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                    Zip Code <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    name="zip_code"
                    value={formData.zip_code}
                    onChange={handleInputChange}
                    placeholder="08055"
                    style={inputStyle}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[14px] font-medium mb-2" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                    Owner/Manager Name <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    name="owner_manager_name"
                    value={formData.owner_manager_name}
                    onChange={handleInputChange}
                    placeholder="John Smith"
                    style={inputStyle}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                  />
                </div>
                <div>
                  <label className="block text-[14px] font-medium mb-2" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                    Phone Number <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="(856) 555-0100"
                    style={inputStyle}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                  />
                </div>
              </div>

              <div>
                <label className="block text-[14px] font-medium mb-2" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                  Email Address <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="owner@thelocaltap.com"
                  style={inputStyle}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                />
              </div>

              <div>
                <label className="block text-[14px] font-medium mb-2" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                  Average Customers Per Day <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  name="average_customers_per_day"
                  type="number"
                  value={formData.average_customers_per_day}
                  onChange={handleInputChange}
                  placeholder="e.g., 150"
                  style={inputStyle}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                />
              </div>
            </div>
          )}

          {/* Step 2: Logo Upload */}
          {currentStep === 2 && (
            <div>
              <p className="text-[14px] mb-4" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                Your logo will be displayed on the coasters. For best results, upload a high-quality PNG with a transparent background.
              </p>

              <div
                className="mb-4 p-4"
                style={{
                  backgroundColor: 'rgba(20, 184, 166, 0.05)',
                  border: '1px solid rgba(20, 184, 166, 0.1)',
                  borderRadius: '12px'
                }}
              >
                <p className="text-[13px]" style={{ color: '#14b8a6' }}>
                  <strong>Recommended:</strong> 1200x1000px or greater, .PNG with transparent background (not required but preferred)
                </p>
              </div>

              <div
                className="p-8 text-center"
                style={{
                  border: '2px dashed rgba(30, 27, 75, 0.2)',
                  borderRadius: '12px'
                }}
              >
                {logoPreview ? (
                  <div>
                    <img src={logoPreview} alt="Logo preview" className="max-h-64 mx-auto mb-4" />
                    <button
                      onClick={() => {
                        setLogoFile(null);
                        setLogoPreview(null);
                      }}
                      className="px-6 py-2 text-[14px] font-semibold transition-all"
                      style={{
                        backgroundColor: 'white',
                        border: '2px solid rgba(30, 27, 75, 0.1)',
                        borderRadius: '12px',
                        color: 'rgba(30, 27, 75, 0.7)'
                      }}
                    >
                      Change Logo
                    </button>
                  </div>
                ) : (
                  <label className="cursor-pointer block">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                    <div
                      className="w-20 h-20 mx-auto mb-4 flex items-center justify-center"
                      style={{ backgroundColor: 'rgba(30, 27, 75, 0.05)', borderRadius: '12px' }}
                    >
                      <ImageIcon size={40} style={{ color: 'rgba(30, 27, 75, 0.3)' }} />
                    </div>
                    <p className="text-[16px] font-semibold mb-2" style={{ color: '#1e1b4b' }}>
                      Click to upload your logo
                    </p>
                    <p className="text-[13px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                      PNG, JPG (max 5MB)
                    </p>
                  </label>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Design Selection */}
          {currentStep === 3 && (
            <div>
              <p className="text-[14px] mb-6" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                Select your seasonal coaster design. You can change this monthly from your dashboard.
              </p>

              <div className="grid md:grid-cols-3 gap-6">
                {coasterDesigns.map((design) => (
                  <motion.div
                    key={design.id}
                    onClick={() => setSelectedDesignId(design.id)}
                    className="cursor-pointer overflow-hidden transition-all"
                    style={{
                      borderRadius: '12px',
                      border: selectedDesignId === design.id ? '3px solid #14b8a6' : '2px solid rgba(30, 27, 75, 0.1)',
                      boxShadow: selectedDesignId === design.id ? '0 4px 15px rgba(20, 184, 166, 0.3)' : '0 2px 4px rgba(30, 27, 75, 0.04)'
                    }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div
                      className="aspect-square flex items-center justify-center p-4"
                      style={{ backgroundColor: 'rgba(30, 27, 75, 0.02)' }}
                    >
                      <img
                        src={resolveAssetUrl(design.image_url)}
                        alt={design.name}
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                    <div className="p-4 bg-white">
                      <h3 className="font-semibold text-[16px]" style={{ color: '#1e1b4b' }}>{design.name}</h3>
                      <p className="text-[13px] capitalize" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>{design.season} {design.year}</p>
                      {selectedDesignId === design.id && (
                        <div className="flex items-center gap-2 mt-2" style={{ color: '#14b8a6' }}>
                          <CheckCircle size={16} />
                          <span className="text-[13px] font-semibold">Selected</span>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div
            className="flex items-center justify-between mt-8 pt-6"
            style={{ borderTop: '1px solid rgba(30, 27, 75, 0.05)' }}
          >
            {/* Back Button - Ghost style */}
            <button
              onClick={handleBack}
              disabled={currentStep === 1}
              className="flex items-center gap-2 px-6 py-3 font-semibold transition-all"
              style={{
                borderRadius: '12px',
                color: currentStep === 1 ? 'rgba(30, 27, 75, 0.3)' : 'rgba(30, 27, 75, 0.7)',
                cursor: currentStep === 1 ? 'not-allowed' : 'pointer'
              }}
            >
              <ArrowLeft size={20} />
              Back
            </button>

            {currentStep < TOTAL_STEPS ? (
              /* Continue Button - V5 Teal */
              <button
                onClick={handleNext}
                className="flex items-center gap-2 px-8 py-3 font-bold text-white transition-all hover:-translate-y-0.5"
                style={{
                  background: 'linear-gradient(135deg, #14b8a6, #0d9488)',
                  borderRadius: '12px',
                  boxShadow: '0 4px 15px rgba(20, 184, 166, 0.4)'
                }}
              >
                Continue
                <ArrowRight size={20} />
              </button>
            ) : (
              /* Submit Button - Green for completion */
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="flex items-center gap-2 px-8 py-3 font-bold text-white transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                  borderRadius: '12px',
                  boxShadow: '0 4px 15px rgba(34, 197, 94, 0.4)'
                }}
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle size={20} />
                    Submit Application
                  </>
                )}
              </button>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default VenueApplication;
